import { c as _c } from "react/compiler-runtime";
import type { ComponentProps } from 'react';
import { cn } from '#cn';

/**
 * Pill button — JAAQ Hub primary control.
 *
 * Per Figma spec:
 *  - filled  : `#f5f5f5` background, dark text — primary "Continue" CTA.
 *  - outlined: 1px `#e5e5e5` border, white text — secondary "Back" CTA, for
 *              dark backgrounds.
 *  - dark    : `#171717` filled — used inside cards.
 *  - ghost   : transparent, 1px border, dark text — understated CTA on a
 *              light background (the outlined variant's counterpart there).
 *
 * Sizes match the Figma `button/regular` (14/24 ExtraBold) and
 * `button/x-large` (18/29 ExtraBold). All caps.
 *
 * The `focus-visible` ring colour belongs to the variant rather than the shared base, because
 * a variant already declares its surface: `filled`/`outlined` sit on a dark backdrop and take a
 * white ring, while `dark`/`ghost` sit on a light one and take `ring-jaaq-black`. One shared
 * white ring is invisible around the light-surface pair, which is most of the hub's call sites.
 *
 * `dark` needs `ring-offset-2` on top of that, because it is the one variant whose fill is the
 * same colour as its ring – `bg-surface-inverse` and `ring-jaaq-black` both resolve to `#171717`,
 * so without the offset the ring only makes the pill 2px larger. The other three do not need it:
 * two sit on a contrasting backdrop and `ghost`'s fill is transparent.
 */
export type Variant = 'filled' | 'outlined' | 'dark' | 'ghost';
type Size = 'regular' | 'x-large';
type ButtonProps = ComponentProps<'button'> & {
  variant?: Variant;
  size?: Size;
};
const variantClasses: Record<Variant, string> = {
  filled: 'bg-surface-muted text-fg hover:bg-surface active:bg-white/90 focus-visible:ring-white/60',
  outlined: 'bg-transparent text-fg-inverse border border-border hover:bg-white/5 active:bg-white/10 focus-visible:ring-white/60',
  dark: 'bg-surface-inverse text-fg-inverse hover:bg-surface-inverse/90 active:bg-surface-inverse focus-visible:ring-jaaq-black focus-visible:ring-offset-2',
  ghost: 'bg-transparent text-fg border border-border hover:bg-surface-muted active:bg-surface focus-visible:ring-jaaq-black'
};
const sizeClasses: Record<Size, string> = {
  regular: 'h-9 px-5 text-sm',
  'x-large': 'h-12 px-8 text-lg'
};
type BuildButtonClassNameArgs = {
  variant?: Variant;
  size?: Size;
  className?: string;
};

/**
 * Builds the pill-button class string, shared so non-`<button>` controls
 * (e.g. a `mailto:` anchor) can wear the exact same styling – mirrors
 * shadcn's `buttonVariants` export.
 */
export const buildButtonClassName = ({
  variant = 'filled',
  size = 'x-large',
  className
}: BuildButtonClassNameArgs = {}): string => cn('inline-flex items-center justify-center gap-3 rounded-full font-extrabold uppercase tracking-wide transition-colors focus:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed', variantClasses[variant], sizeClasses[size], className);

/** The pill-shaped primary control — see the `Variant` doc comment above for when to use each of `filled`/`outlined`/`dark`/`ghost`. */
export const Button = t0 => {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      type: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const variant = t1 === undefined ? "filled" : t1;
  const size = t2 === undefined ? "x-large" : t2;
  const type = t3 === undefined ? "button" : t3;
  let t4;
  if ($[6] !== className || $[7] !== size || $[8] !== variant) {
    t4 = buildButtonClassName({
      variant,
      size,
      className
    });
    $[6] = className;
    $[7] = size;
    $[8] = variant;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== t4 || $[12] !== type) {
    t5 = <button type={type} className={t4} {...props} />;
    $[10] = props;
    $[11] = t4;
    $[12] = type;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};